import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ArrowLeft, Trash2, FileText, RotateCcw, Search, Printer, Edit3 } from 'react-feather';
import ReactTable from 'react-table-v6';
import 'react-table-v6/react-table.css';
import {
    Button, Card, CardBody, CardFooter, Col, Row, Form, FormGroup,
    Label, Input, Alert, Spinner, Table, Modal, ModalHeader, ModalBody,
    ModalFooter, Badge,
} from 'reactstrap';
import Select from 'react-select';
import http from '../../components/network';

const DPP_RATE_RATIO = 11 / 12;
const TAX_RATE_PERCENT = 12;

const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    if (typeof dateStr === 'string') return dateStr.substring(0, 10);
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const emptyHeader = {
    supplier_id: '',
    return_date: formatDateForInput(new Date()),
    notes: '',
};

const PurchaseReturn = () => {
    const { t } = useTranslation();
    const [role, setRole] = useState([]);
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);

    const [viewMode, setViewMode] = useState('list');

    // Form
    const [header, setHeader] = useState(emptyHeader);
    const [details, setDetails] = useState([]);

    // Master data
    const [batches, setBatches] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [filters, setFilters] = useState({
        supplier_id: '',
        batch_number: '',
        expiring_within_days: '',
    });

    const [purchaseInvoices, setPurchaseInvoices] = useState([]);
    const [selectedPiId, setSelectedPiId] = useState('');

    // Modal
    const [modalAddBatch, setModalAddBatch] = useState(false);
    const [modalDetail, setModalDetail] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const selectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    const fetchReturns = async () => {
        setLoading(true);
        try {
            const { data } = await http.get('/purchase-returns');
            setReturns(Array.isArray(data) ? data : []);
            setRole(JSON.parse(data.role || '[]'));
        } catch (err) {
            setError(t('Failed to load return data'));
        } finally {
            setLoading(false);
        }
    };

    const fetchMasterData = async (includeIds = null) => {
        setLoading(true);
        try {
            const params = {};
            if (filters.supplier_id) params.supplier_id = filters.supplier_id;
            if (filters.batch_number) params.batch_number = filters.batch_number;
            if (filters.expiring_within_days) params.expiring_within_days = filters.expiring_within_days;
            if (selectedPiId) params.purchase_invoice_id = selectedPiId;

            const includeList = includeIds || details.map((d) => d.inventory_detail_id).filter(Boolean);
            if (includeList.length > 0) {
                params.include_inventory_detail_ids = includeList;
            }

            const { data } = await http.get('/purchase-returns/master-data', { params });
            setBatches(Array.isArray(data.batches) ? data.batches : []);
            setSuppliers(Array.isArray(data.suppliers) ? data.suppliers : []);
            setPurchaseInvoices(Array.isArray(data.purchase_invoices) ? data.purchase_invoices : []);
        } catch (err) {
            setError(t('Failed to load batch data'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReturns();
    }, []);

    const handleAddNew = () => {
        setError(null);
        setHeader(emptyHeader);
        setDetails([]);
        setSelectedPiId('');
        setFilters({ supplier_id: '', batch_number: '', expiring_within_days: '' });
        setEditingId(null);
        fetchMasterData();
        setViewMode('form');
    };

    const handleCancel = () => {
        setViewMode('list');
        setError(null);
        setEditingId(null);
    };

    const handleSearchBatch = () => {
        fetchMasterData();
    };

    const handleEdit = async (id) => {
        setError(null);
        setLoading(true);
        try {
            // Fetch detail dulu
            const { data } = await http.get(`/purchase-returns/${id}`);

            // Set header
            setHeader({
                supplier_id: data.supplier_id,
                return_date: formatDateForInput(data.return_date),
                notes: data.notes || '',
            });

            // Set details (map dari response)
            setDetails(
                (data.details || []).map((d) => {
                    const qty = parseFloat(d.qty) || 0;
                    const price = parseFloat(d.price) || 0;
                    const subtotal = qty * price;
                    const dpp = subtotal * DPP_RATE_RATIO;
                    const tax = dpp * (TAX_RATE_PERCENT / 100);
                    const nominal = subtotal + tax;

                    return {
                        inventory_detail_id: d.inventory_detail_id,
                        item_id: d.item_id,
                        item_name: d.item?.item_name || '-',
                        item_unit_id: d.item_unit_id,
                        unit_name: d.item_unit?.unit_name || '-',
                        batch_number: d.batch_number,
                        expired: d.expired || d.inventory_detail?.expired || null,
                        max_qty: qty + (parseFloat(d.inventory_detail?.remaining_qty) || 0),
                        qty,
                        price,
                        tax_percent: TAX_RATE_PERCENT,
                        dpp,
                        tax_amount: tax,
                        subtotal,
                        nominal,
                        notes: d.notes || '',
                    };
                })
            );

            setEditingId(id);

            setSelectedPiId(data.purchase_invoice_id || '');

            setFilters({
                supplier_id: data.supplier_id || '',
                batch_number: '',
                expiring_within_days: '',
            });

            await fetchMasterData();
            setViewMode('form');
        } catch (err) {
            setError(t('Failed to load return data for editing'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddBatch = (batch) => {
        if (details.find((d) => d.inventory_detail_id === batch.id)) {
            return;
        }

        const price = parseFloat(batch.unit_price) || 0;
        const qty = 1;
        const subtotal = qty * price;
        const dpp = subtotal * DPP_RATE_RATIO;
        const tax = dpp * (TAX_RATE_PERCENT / 100);
        const nominal = subtotal + tax;

        setDetails([
            ...details,
            {
                inventory_detail_id: batch.id,
                item_id: batch.item_id,
                item_name: batch.item_name,
                item_unit_id: batch.item_unit_id,
                unit_name: batch.unit_name,
                batch_number: batch.batch_number,
                expired: batch.expired,
                max_qty: batch.remaining_qty,
                qty,
                price,
                tax_percent: TAX_RATE_PERCENT,
                dpp,
                tax_amount: tax,
                subtotal,
                nominal,
                notes: '',
            },
        ]);

        if (!header.supplier_id) {
            setHeader((prev) => ({ ...prev, supplier_id: batch.supplier_id }));
        }

        setModalAddBatch(false);
    };

    const handleDetailChange = (index, e) => {
        const newDetails = [...details];
        const { name, value } = e.target;

        if (name === 'qty') {
            const num = parseFloat(value);
            if (Number.isNaN(num)) {
                newDetails[index].qty = '';
            } else {
                const rounded = Math.round(num * 100) / 100;
                const isNearInteger = Math.abs(rounded - Math.round(rounded)) < 0.01;
                newDetails[index].qty = isNearInteger ? Math.round(rounded) : rounded;
            }
        } else {
            newDetails[index][name] = value;
        }

        // Recalculate DPP, Tax, Subtotal, Nominal
        const item = newDetails[index];
        const qty = parseFloat(item.qty) || 0;
        const price = parseFloat(item.price) || 0;
        const subtotal = qty * price;
        const dpp = subtotal * DPP_RATE_RATIO;
        const tax = dpp * (TAX_RATE_PERCENT / 100);

        newDetails[index].subtotal = subtotal;
        newDetails[index].dpp = dpp;
        newDetails[index].tax_amount = tax;
        newDetails[index].nominal = subtotal + tax;

        setDetails(newDetails);
    };

    const handleRemoveItem = (index) => {
        setDetails(details.filter((_, i) => i !== index));
    };

    // handler print
    const handlePrintReturn = async (id) => {
        if (!id) return;
        try {
            const response = await http.get(`/purchase-returns/${id}/print`, {
                responseType: 'blob',
            });

            const contentDisposition = response.headers?.['content-disposition'] || '';
            let filename = `PR_${id}_${Date.now()}.pdf`;
            const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (match && match[1]) {
                filename = match[1].replace(/['"]/g, '');
            }

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (err) {
            console.error('Failed to print return:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        if (!header.supplier_id) {
            setError(t('Please select a supplier'));
            setLoading(false);
            return;
        }

        if (details.length === 0) {
            setError(t('Please add at least one batch'));
            setLoading(false);
            return;
        }

        const invalidQty = details.find((d) => {
            const qty = parseFloat(d.qty);
            return !qty || qty <= 0;
        });

        if (invalidQty) {
            setError(`${t('Invalid return qty for batch')} ${invalidQty.batch_number}`);
            setLoading(false);
            return;
        }

        const overQty = details.find((d) => {
            const qty = parseFloat(d.qty);
            return qty > parseFloat(d.max_qty);
        });

        if (overQty) {
            setError(
                `${t('Return qty for batch')} ${overQty.batch_number} ${t('exceeds remaining')} (${overQty.max_qty})`
            );
            setLoading(false);
            return;
        }

        const payload = {
            supplier_id: header.supplier_id,
            return_date: header.return_date,
            notes: header.notes,
            details: details.map((d) => ({
                inventory_detail_id: d.inventory_detail_id,
                qty: Math.round(parseFloat(d.qty) * 100) / 100,
                notes: d.notes || '',
            })),
        };

        try {
            let response;
            if (editingId) {
                response = await http.put(`/purchase-returns/${editingId}`, payload);
            } else {
                response = await http.post('/purchase-returns', payload);
            }

            await fetchReturns();
            setViewMode('list');
            setEditingId(null);

            // ← Redirect / download struk
            const returnId = response.data.id || editingId;
            if (returnId) {
                await handlePrintReturn(returnId);
            }
        } catch (err) {
            setError(err.response?.data?.message || t('Failed to save return'));
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = async (id) => {
        setLoadingDetail(true);
        setModalDetail(true);
        setSelectedReturn(null);
        try {
            const { data } = await http.get(`/purchase-returns/${id}`);
            setSelectedReturn(data);
        } catch (err) {
            setError(t('Failed to load return detail'));
            setModalDetail(false);
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Delete this return? Stock will be restored.'))) return;
        try {
            await http.delete(`/purchase-returns/${id}`);
            await fetchReturns();
        } catch (err) {
            setError(err.response?.data?.message || t('Failed to delete return'));
        }
    };

    const columns = [
        { Header: t('Return Number'), accessor: 'return_number', width: 150 },
        {
            Header: t('Return Date'),
            accessor: 'return_date',
            Cell: (row) => <span>{formatDateForDisplay(row.value)}</span>,
        },
        { Header: t('Supplier'), accessor: 'supplier.supplier_name' },
        {
            Header: t('Total'),
            accessor: 'total_amount',
            Cell: (row) => <span>{new Intl.NumberFormat('id-ID').format(row.value || 0)}</span>,
        },
        {
            Header: t('Status'),
            accessor: 'status',
            width: 130,
            Cell: (row) => {
                const styleMap = {
                    pending: { background: '#f59e0b', color: '#fff' },
                    partial: { background: '#3b82f6', color: '#fff' },
                    completed: { background: '#10b981', color: '#fff' },
                };
                const labelMap = {
                    pending: 'Pending',
                    partial: 'Partial',
                    completed: 'Completed',
                };
                const style = styleMap[row.value] || { background: '#64748b', color: '#fff' };

                return (
                    <span
                        style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '999px',
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            ...style,
                        }}
                    >
                        {labelMap[row.value] || row.value || '-'}
                    </span>
                );
            },
        },
        {
            Header: t('Action'),
            accessor: 'id',
            sortable: false,
            filterable: false,
            width: 200,
            Cell: (row) => {
                return (
                    <div className="d-flex gap-1">
                        <Button
                            color="light"
                            size="sm"
                            className="border d-flex align-items-center justify-content-center"
                            style={{ width: '34px', height: '34px', padding: 0 }}
                            onClick={() => handleViewDetail(row.value)}
                            title={t('View Detail')}
                        >
                            <FileText size={15} className="text-info" />
                        </Button>
                        <Button
                            color="light"
                            size="sm"
                            className="border d-flex align-items-center justify-content-center"
                            style={{ width: '34px', height: '34px', padding: 0 }}
                            onClick={() => handlePrintReturn(row.value)}
                            title={t('Print')}
                        >
                            <Printer size={15} className="text-success" />
                        </Button>
                        <Button
                            color="light"
                            size="sm"
                            className="border d-flex align-items-center justify-content-center"
                            style={{ width: '34px', height: '34px', padding: 0 }}
                            onClick={() => handleEdit(row.value)}
                            title={t('Edit')}
                        >
                            <Edit3 size={15} className="text-primary" />
                        </Button>
                        <Button
                            color="light"
                            size="sm"
                            className="border d-flex align-items-center justify-content-center"
                            style={{ width: '34px', height: '34px', padding: 0 }}
                            onClick={() => handleDelete(row.value)}
                            title={t('Delete')}
                        >
                            <Trash2 size={15} className="text-danger" />
                        </Button>
                    </div>
                );
            },
        },
    ];

    // ============================
    // FORM VIEW
    // ============================
    if (viewMode === 'form') {
        const supplierOptions = suppliers.map((s) => ({ value: s.id, label: s.supplier_name }));

        return (
            <div className="p-3 bg-light min-vh-100">
                <div className="d-flex align-items-center mb-4">
                    <button
                        type="button"
                        onClick={handleCancel}
                        style={{
                            width: '48px', height: '48px', borderRadius: '50%',
                            border: '1px solid #e5e7eb', background: '#ffffff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', marginRight: '16px', flexShrink: 0,
                        }}
                    >
                        <ArrowLeft size={20} color="#1e293b" />
                    </button>
                    <div>
                        <h3 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>
                            {editingId ? t('Edit Purchase Return') : t('Add Purchase Return')}
                        </h3>
                        <p className="mb-0 text-muted small">
                            {editingId
                                ? t('Update existing purchase return data')
                                : t('Select batches to return to supplier')}
                        </p>
                    </div>
                </div>

                <Card className="shadow-sm border-0">
                    <Form onSubmit={handleSubmit}>
                        <CardBody className="p-4">
                            {error && <Alert color="danger" className="mb-3">{error}</Alert>}

                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label>
                                            {t('Purchase Invoice')} <small className="text-muted">({t('Optional filter')})</small>
                                        </Label>
                                        <Select
                                            options={[
                                                { value: '', label: '— All PIs —' },
                                                ...purchaseInvoices.map((pi) => ({
                                                    value: pi.id,
                                                    label: `${pi.invoice_number} • ${pi.supplier_name} • ${formatDateForDisplay(pi.invoice_date)}`,
                                                })),
                                            ]}
                                            value={
                                                selectedPiId
                                                    ? purchaseInvoices
                                                        .filter((pi) => pi.id === selectedPiId)
                                                        .map((pi) => ({
                                                            value: pi.id,
                                                            label: `${pi.invoice_number} • ${pi.supplier_name} • ${formatDateForDisplay(pi.invoice_date)}`,
                                                        }))[0]
                                                    : { value: '', label: '— All PIs —' }
                                            }
                                            onChange={(opt) => {
                                                setSelectedPiId(opt?.value || '');
                                            }}
                                            isSearchable
                                            menuPortalTarget={document.body}
                                            styles={selectStyles}
                                            placeholder="Select Purchase Invoice..."
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>{t('Supplier')} <span className="text-danger">*</span></Label>
                                        <Select
                                            options={supplierOptions}
                                            value={supplierOptions.find((opt) => opt.value === header.supplier_id)}
                                            onChange={(opt) => setHeader((p) => ({ ...p, supplier_id: opt?.value || '' }))}
                                            isSearchable
                                            menuPortalTarget={document.body}
                                            styles={selectStyles}
                                            placeholder="Pilih supplier..."
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>{t('Return Date')} <span className="text-danger">*</span></Label>
                                        <Input
                                            type="date"
                                            value={header.return_date}
                                            onChange={(e) => setHeader((p) => ({ ...p, return_date: e.target.value }))}
                                            required
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label>{t('Notes')}</Label>
                                        <Input
                                            type="textarea"
                                            value={header.notes}
                                            onChange={(e) => setHeader((p) => ({ ...p, notes: e.target.value }))}
                                            rows="2"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <div className="d-flex align-items-center gap-2 mb-3 mt-4 pb-2" style={{ borderBottom: '2px solid #e5e7eb' }}>
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ width: '36px', height: '36px', background: '#fef3c7', flexShrink: 0 }}
                                >
                                    <span style={{ fontSize: '16px' }}>📦</span>
                                </div>
                                <div className="flex-grow-1">
                                    <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>{t('Return Items')}</h5>
                                    <small className="text-muted">{t('List of batches to be returned')}</small>
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => { setModalAddBatch(true); fetchMasterData(); }}
                                    style={{
                                        background: '#1e293b',
                                        border: 'none',
                                        color: '#fff',
                                        borderRadius: '8px',
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        lineHeight: 1.2,
                                        width: 'auto',
                                    }}
                                >
                                    <Plus size={12} /> {t('Add Batch')}
                                </Button>
                            </div>

                            {details.length > 0 ? (
                                <Table responsive bordered>
                                    <thead>
                                        <tr>
                                            <th>{t('Item')}</th>
                                            <th>{t('Batch Number')}</th>
                                            <th>{t('Exp Date')}</th>
                                            <th className="text-end">{t('Sisa Stok')}</th>
                                            <th>{t('Qty Retur')}</th>
                                            <th className="text-end">{t('Price')}</th>
                                            <th>{t('Notes')}</th>
                                            <th>{t('Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {details.map((item, index) => {
                                            const qty = parseFloat(item.qty) || 0;
                                            const price = parseFloat(item.price) || 0;
                                            const isOver = qty > parseFloat(item.max_qty);

                                            return (
                                                <tr key={item.inventory_detail_id}>
                                                    <td>{item.item_name} <small className="text-muted">({item.unit_name})</small></td>
                                                    <td><Badge color="info" pill>{item.batch_number || '-'}</Badge></td>
                                                    <td>{item.expired ? formatDateForDisplay(item.expired) : '-'}</td>
                                                    <td className="text-end">{item.max_qty}</td>
                                                    <td style={{ minWidth: '110px' }}>
                                                        <Input
                                                            type="number"
                                                            name="qty"
                                                            value={item.qty}
                                                            onChange={(e) => handleDetailChange(index, e)}
                                                            min="0.01"
                                                            max={item.max_qty}
                                                            step="0.01"
                                                            invalid={isOver}
                                                        />
                                                    </td>
                                                    <td className="text-end">
                                                        {new Intl.NumberFormat('id-ID').format(price)}
                                                    </td>
                                                    <td style={{ minWidth: '130px' }}>
                                                        <Input
                                                            type="text"
                                                            name="notes"
                                                            value={item.notes}
                                                            onChange={(e) => handleDetailChange(index, e)}
                                                        />
                                                    </td>
                                                    <td style={{ minWidth: '60px' }}>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveItem(index)}
                                                            style={{ width: '34px', height: '34px', padding: 0 }}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            ) : (
                                <div
                                    className="text-center py-5 rounded"
                                    style={{ background: '#f8fafc', border: '1px dashed #cbd5e1' }}
                                >
                                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>📦</div>
                                    <h5 className="text-muted mb-2">{t('No Items to Return Selected yet')}</h5>
                                    <p className="text-muted small mb-3">
                                        {t('Click "Add Batch" to select the batch to be returned.')}
                                    </p>
                                    <Button
                                        size="sm"
                                        onClick={() => { setModalAddBatch(true); fetchMasterData(); }}
                                        style={{
                                            background: '#1e293b',
                                            border: 'none',
                                            color: '#fff',
                                            borderRadius: '8px',
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            lineHeight: 1.2,
                                            width: 'auto',
                                        }}
                                    >
                                        <Plus size={12} /> {t('Add Batch')}
                                    </Button>
                                </div>
                            )}

                            {/* === TOTAL SUMMARY === */}
                            {details.length > 0 && (() => {
                                const subtotal = details.reduce((sum, item) => {
                                    const q = parseFloat(item.qty) || 0;
                                    const p = parseFloat(item.price) || 0;
                                    return sum + (q * p);
                                }, 0);
                                const dpp = subtotal * DPP_RATE_RATIO;
                                const tax = dpp * (TAX_RATE_PERCENT / 100);
                                const grandTotal = subtotal + tax;

                                return (
                                    <Row className="justify-content-end mt-4">
                                        <Col md={5}>
                                            <div className="d-flex justify-content-between py-1">
                                                <span className="text-muted">{t('Subtotal')}</span>
                                                <strong>{new Intl.NumberFormat('id-ID').format(subtotal)}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between py-1">
                                                <span className="text-muted">
                                                    {t('DPP')} <small className="text-muted">(11/12)</small>
                                                </span>
                                                <strong>{new Intl.NumberFormat('id-ID').format(dpp)}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between py-1">
                                                <span className="text-muted">
                                                    {t('Tax')} <small className="text-muted">({TAX_RATE_PERCENT}%)</small>
                                                </span>
                                                <strong>{new Intl.NumberFormat('id-ID').format(tax)}</strong>
                                            </div>
                                            <div
                                                className="d-flex justify-content-between py-2 mt-1"
                                                style={{ borderTop: '2px solid #e5e7eb' }}
                                            >
                                                <span className="fw-bold">{t('Grand Total')}</span>
                                                <strong className="text-primary" style={{ fontSize: '1.1rem' }}>
                                                    {new Intl.NumberFormat('id-ID').format(grandTotal)}
                                                </strong>
                                            </div>
                                        </Col>
                                    </Row>
                                );
                            })()}
                        </CardBody>

                        <CardFooter className="border-0 bg-white text-end pt-3">
                            <Button color={editingId ? 'primary' : 'danger'} type="submit" disabled={loading} className="me-2">
                                {loading ? (
                                    <Spinner size="sm" />
                                ) : editingId ? (
                                    <>{t('Update Return')}</>
                                ) : (
                                    <><RotateCcw size={14} className="me-1" /> {t('Process Return')}</>
                                )}
                            </Button>
                            <Button color="secondary" onClick={handleCancel} disabled={loading}>
                                {t('Cancel')}
                            </Button>
                        </CardFooter>
                    </Form>
                </Card >

                {/* Modal Pilih Batch */}
                < Modal isOpen={modalAddBatch} toggle={() => setModalAddBatch(false)} size="xl" centered >
                    <ModalHeader
                        toggle={() => setModalAddBatch(false)}
                        style={{
                            background: '#fff',
                            color: '#1e293b',
                            borderBottom: '1px solid #e5e7eb',
                            padding: '20px 24px',
                        }}
                    >
                        <div>
                            <h5 className="mb-1" style={{ color: '#1e293b', fontWeight: 700 }}>
                                <Search size={18} className="me-2" style={{ verticalAlign: 'text-bottom', color: '#64748b' }} />
                                {t('Select Batch to Return')}
                            </h5>
                            <small style={{ color: '#94a3b8' }}>
                                {t('Search and pick batches from inventory')}
                            </small>
                        </div>
                    </ModalHeader>

                    <ModalBody style={{ padding: '24px', background: '#f8fafc' }}>
                        {/* Filter Card */}
                        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '12px' }}>
                            <CardBody style={{ padding: '20px' }}>
                                <h6 className="fw-bold mb-3" style={{ color: '#1e293b', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    🔍 {t('Filter')}
                                </h6>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                                        <Label className="small fw-semibold text-muted mb-1">{t('Supplier')}</Label>
                                        <Select
                                            options={supplierOptions}
                                            value={supplierOptions.find((opt) => opt.value === filters.supplier_id)}
                                            onChange={(opt) => setFilters((p) => ({ ...p, supplier_id: opt?.value || '' }))}
                                            isSearchable
                                            isClearable
                                            menuPortalTarget={document.body}
                                            styles={{
                                                ...selectStyles,
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '38px',
                                                    height: '38px',
                                                    borderRadius: '8px',
                                                }),
                                            }}
                                            placeholder="All suppliers..."
                                        />
                                    </div>
                                    <div style={{ flex: '1 1 180px', minWidth: '160px' }}>
                                        <Label className="small fw-semibold text-muted mb-1">{t('Batch Number')}</Label>
                                        <Input
                                            type="text"
                                            value={filters.batch_number}
                                            onChange={(e) => setFilters((p) => ({ ...p, batch_number: e.target.value }))}
                                            placeholder="Search batch..."
                                            style={{ borderRadius: '8px', height: '38px' }}
                                        />
                                    </div>
                                    <div style={{ flex: '1 1 160px', minWidth: '150px' }}>
                                        <Label className="small fw-semibold text-muted mb-1">{t('Expiring Within (Days)')}</Label>
                                        <Input
                                            type="number"
                                            value={filters.expiring_within_days}
                                            onChange={(e) => setFilters((p) => ({ ...p, expiring_within_days: e.target.value }))}
                                            placeholder="e.g. 30"
                                            style={{ borderRadius: '8px', height: '38px' }}
                                        />
                                    </div>
                                    <div style={{ flex: '0 0 auto' }}>
                                        <Label className="small fw-semibold text-muted mb-1" style={{ visibility: 'hidden' }}>
                                            &nbsp;
                                        </Label>
                                        <Button
                                            onClick={handleSearchBatch}
                                            disabled={loading}
                                            style={{
                                                borderRadius: '8px',
                                                background: '#1e293b',
                                                border: 'none',
                                                color: '#fff',
                                                fontWeight: 600,
                                                height: '38px',
                                                padding: '0 20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                            }}
                                        >
                                            {loading ? <Spinner size="sm" /> : <><Search size={14} /> {t('Search')}</>}
                                        </Button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        {/* Result */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0" style={{ color: '#1e293b' }}>
                                📦 {t('Available Batches')}
                                <Badge color="primary" pill className="ms-2">{batches.length}</Badge>
                            </h6>
                        </div>

                        {batches.length === 0 ? (
                            <div
                                className="text-center py-5"
                                style={{
                                    background: '#fff',
                                    border: '2px dashed #cbd5e1',
                                    borderRadius: '12px',
                                }}
                            >
                                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                                <h6 className="text-muted mb-2">
                                    {loading ? t('Loading...') : t('No batch available')}
                                </h6>
                                <small className="text-muted">
                                    {t('Try adjusting your filters')}
                                </small>
                            </div>
                        ) : (
                            <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                <Table hover responsive className="mb-0" style={{ fontSize: '13px' }}>
                                    <thead style={{ background: '#f1f5f9' }}>
                                        <tr>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Item')}
                                            </th>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Batch')}
                                            </th>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Exp Date')}
                                            </th>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Supplier')}
                                            </th>
                                            <th className="text-end" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Stock')}
                                            </th>
                                            <th className="text-end" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Price')}
                                            </th>
                                            <th className="text-center" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }} width="80">
                                                {t('Action')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batches.map((b) => {
                                            const alreadyAdded = details.find((d) => d.inventory_detail_id === b.id);
                                            const isExpiring = b.expired && new Date(b.expired) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

                                            return (
                                                <tr key={b.id} style={{ background: alreadyAdded ? '#f0fdf4' : 'transparent' }}>
                                                    <td style={{ padding: '12px' }}>
                                                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{b.item_name}</div>
                                                        <small className="text-muted">({b.unit_name})</small>
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <Badge
                                                            pill
                                                            style={{
                                                                background: '#dbeafe',
                                                                color: '#1e40af',
                                                                fontWeight: 600,
                                                                fontSize: '11px',
                                                            }}
                                                        >
                                                            {b.batch_number || '-'}
                                                        </Badge>
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        {b.expired ? (
                                                            <span style={{ color: isExpiring ? '#dc2626' : '#334155', fontWeight: isExpiring ? 600 : 400 }}>
                                                                {isExpiring && '⚠️ '}
                                                                {formatDateForDisplay(b.expired)}
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                    <td style={{ padding: '12px', color: '#475569' }}>{b.supplier_name}</td>
                                                    <td className="text-end" style={{ padding: '12px', fontWeight: 600, color: '#0ea5e9' }}>
                                                        {b.remaining_qty}
                                                    </td>
                                                    <td className="text-end" style={{ padding: '12px', color: '#475569' }}>
                                                        Rp {new Intl.NumberFormat('id-ID').format(b.unit_price)}
                                                    </td>
                                                    <td className="text-center" style={{ padding: '12px' }}>
                                                        <Button
                                                            size="sm"
                                                            disabled={alreadyAdded}
                                                            onClick={() => handleAddBatch(b)}
                                                            style={{
                                                                borderRadius: '6px',
                                                                border: alreadyAdded ? '1px solid #e5e7eb' : 'none',
                                                                background: alreadyAdded ? '#f8fafc' : '#1e293b',
                                                                color: alreadyAdded ? '#94a3b8' : '#fff',
                                                                fontWeight: 600,
                                                                padding: '4px 10px',
                                                                fontSize: '11px',
                                                                lineHeight: 1.2,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '3px',
                                                            }}
                                                        >
                                                            {alreadyAdded ? (
                                                                <>✓ {t('Added')}</>
                                                            ) : (
                                                                <>
                                                                    <Plus size={11} /> {t('Add')}
                                                                </>
                                                            )}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </ModalBody>

                    <ModalFooter style={{ borderTop: '1px solid #e5e7eb', padding: '16px 24px', background: '#fff' }}>
                        <div className="text-muted small me-auto">
                            💡 {t('Click Add to include batch in return list')}
                        </div>
                        <Button
                            color="secondary"
                            onClick={() => setModalAddBatch(false)}
                            style={{ borderRadius: '8px', fontWeight: 600 }}
                        >
                            {t('Close')}
                        </Button>
                    </ModalFooter>
                </Modal >
            </div >
        );
    }

    // ============================
    // LIST VIEW
    // ============================
    return (
        <div className="p-3 bg-light min-vh-100">
            <div className="mb-4">
                <h3 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>
                    {t('Purchase Return')}
                </h3>
                <p className="mb-0 text-muted small">
                    {t('Manage returning items to supplier')}
                </p>
            </div>

            {error && (
                <Alert color="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Card className="shadow-sm border-0 mb-3">
                <CardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <Button
                            color="success"
                            onClick={handleAddNew}
                            disabled={role.length > 0 && !role.some((r) => r.name === 'PurchaseReturn' && r.create)}
                            className="d-flex align-items-center gap-2 fw-bold"
                            style={{ borderRadius: '8px', padding: '8px 16px' }}
                        >
                            <Plus size={16} />
                            {t('Add New Return')}
                        </Button>
                        <div className="text-muted small">
                            <strong>{returns.length}</strong> {t('total retur')}
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* === STATUS INFO BANNER === */}
            <div
                className="mb-3 p-3 rounded d-flex align-items-start gap-3"
                style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)',
                    border: '1px solid #bfdbfe',
                    borderRadius: '12px',
                }}
            >
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '36px', height: '36px', background: '#dbeafe' }}
                >
                    <FileText size={18} className="text-primary" />
                </div>
                <div className="flex-grow-1">
                    <h6 className="fw-bold mb-2" style={{ color: '#1e40af', fontSize: '13px' }}>
                        {t('Purchase Return Status Guide')}
                    </h6>
                    <div className="d-flex flex-wrap gap-3 small">
                        <div className="d-flex align-items-center gap-2">
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: '#f59e0b',
                                }}
                            />
                            <span>
                                <strong style={{ color: '#92400e' }}>{t('Pending')}</strong>
                                {': '}
                                {t('No refund received from supplier yet')}
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: '#3b82f6',
                                }}
                            />
                            <span>
                                <strong style={{ color: '#1e40af' }}>{t('Partial')}</strong>
                                {': '}
                                {t('Supplier has partially refunded the return')}
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: '#10b981',
                                }}
                            />
                            <span>
                                <strong style={{ color: '#065f46' }}>{t('Completed')}</strong>
                                {': '}
                                {t('Return fully refunded and closed')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <Card className="shadow-sm border-0">
                <CardBody className="p-3">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="text-muted mt-2 small">{t('Loading data...')}</div>
                        </div>
                    ) : returns.length === 0 ? (
                        <div
                            className="text-center py-5 rounded"
                            style={{ background: '#f8fafc', border: '1px dashed #cbd5e1' }}
                        >
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📦</div>
                            <h5 className="text-muted mb-2">{t('Belum Ada Retur')}</h5>
                            <p className="text-muted small mb-3">
                                {t('Mulai dengan membuat retur baru')}
                            </p>
                            <Button color="primary" size="sm" onClick={handleAddNew}>
                                <Plus size={14} /> {t('Buat Retur Baru')}
                            </Button>
                        </div>
                    ) : (
                        <ReactTable
                            data={returns}
                            columns={columns}
                            defaultPageSize={10}
                            className="-striped -highlight"
                            filterable
                        />
                    )}
                </CardBody>
            </Card>

            {/* Modal Detail Retur */}
            <Modal isOpen={modalDetail} toggle={() => setModalDetail(false)} size="lg" centered>
                <ModalHeader
                    toggle={() => setModalDetail(false)}
                    style={{
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        color: '#fff',
                        borderBottom: 'none',
                        padding: '20px 24px',
                        borderTopLeftRadius: 'calc(0.5rem - 1px)',
                        borderTopRightRadius: 'calc(0.5rem - 1px)',
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '42px', height: '42px', background: 'rgba(255, 255, 255, 0.15)' }}
                        >
                            <RotateCcw size={20} color="#fff" />
                        </div>
                        <div>
                            <div className="fw-bold" style={{ fontSize: '16px', color: '#fff' }}>
                                {t('Purchase Return Detail')}
                            </div>
                            <small style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '12px' }}>
                                {selectedReturn?.return_number || '-'}
                            </small>
                        </div>
                    </div>
                </ModalHeader>

                <ModalBody style={{ padding: '24px', background: '#f8fafc' }}>
                    {loadingDetail ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="text-muted mt-2 small">{t('Loading data...')}</div>
                        </div>
                    ) : selectedReturn ? (
                        <>
                            {/* === INFO CARDS === */}
                            <Row className="g-2 mb-3">
                                <Col md={6}>
                                    <div
                                        className="p-3 rounded h-100"
                                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                                    >
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span style={{ fontSize: '14px' }}>🏢</span>
                                            <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                {t('Supplier')}
                                            </small>
                                        </div>
                                        <div className="fw-bold" style={{ color: '#1e293b', fontSize: '14px' }}>
                                            {selectedReturn.supplier?.supplier_name || '-'}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div
                                        className="p-3 rounded h-100"
                                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                                    >
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span style={{ fontSize: '14px' }}>📅</span>
                                            <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                {t('Return Date')}
                                            </small>
                                        </div>
                                        <div className="fw-bold" style={{ color: '#1e293b', fontSize: '14px' }}>
                                            {formatDateForDisplay(selectedReturn.return_date)}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div
                                        className="p-3 rounded h-100"
                                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                                    >
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span style={{ fontSize: '14px' }}>📊</span>
                                            <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                {t('Status')}
                                            </small>
                                        </div>
                                        <div>
                                            {(() => {
                                                const { status } = selectedReturn;
                                                const styleMap = {
                                                    pending: { background: '#f59e0b', color: '#fff' },
                                                    partial: { background: '#3b82f6', color: '#fff' },
                                                    completed: { background: '#10b981', color: '#fff' },
                                                };
                                                const labelMap = {
                                                    pending: 'Pending',
                                                    partial: 'Partial',
                                                    completed: 'Completed',
                                                };
                                                const s = styleMap[status] || { background: '#64748b', color: '#fff' };
                                                return (
                                                    <span
                                                        style={{
                                                            display: 'inline-block',
                                                            padding: '4px 12px',
                                                            borderRadius: '999px',
                                                            fontSize: '11px',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.4px',
                                                            ...s,
                                                        }}
                                                    >
                                                        {labelMap[status] || status || '-'}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div
                                        className="p-3 rounded h-100"
                                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                                    >
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span style={{ fontSize: '14px' }}>📦</span>
                                            <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                {t('Total Items')}
                                            </small>
                                        </div>
                                        <div className="fw-bold" style={{ color: '#1e293b', fontSize: '14px' }}>
                                            {selectedReturn.details?.length || 0}{' '}
                                            <span className="text-muted fw-normal" style={{ fontSize: '12px' }}>
                                                {t('items')}
                                            </span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* === NOTES === */}
                            {selectedReturn.notes && (
                                <div
                                    className="mb-3 p-3 rounded"
                                    style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px' }}
                                >
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <span style={{ fontSize: '14px' }}>📝</span>
                                        <small className="fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px', color: '#92400e' }}>
                                            {t('Notes')}
                                        </small>
                                    </div>
                                    <div style={{ color: '#78350f', fontSize: '13px' }}>
                                        {selectedReturn.notes}
                                    </div>
                                </div>
                            )}

                            {/* === ITEM DETAILS === */}
                            <div style={{ background: '#fff', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                <Table hover responsive className="mb-0" style={{ fontSize: '13px' }}>
                                    <thead style={{ background: '#f1f5f9' }}>
                                        <tr>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }} width="50">
                                                #
                                            </th>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Item')}
                                            </th>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Batch')}
                                            </th>
                                            <th className="text-end" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Qty')}
                                            </th>
                                            <th className="text-end" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Price')}
                                            </th>
                                            <th className="text-end" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', padding: '12px' }}>
                                                {t('Subtotal')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selectedReturn.details || []).map((d, idx) => {
                                            const subtotal = (parseFloat(d.qty) || 0) * (parseFloat(d.price) || 0);
                                            return (
                                                <tr key={d.id || idx}>
                                                    <td className="text-center" style={{ padding: '12px', color: '#6b7280' }}>
                                                        {idx + 1}
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <div style={{ fontWeight: 600, color: '#1e293b' }}>
                                                            {d.item?.item_name || '-'}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        {d.batch_number ? (
                                                            <Badge
                                                                pill
                                                                style={{
                                                                    background: '#dbeafe',
                                                                    color: '#1e40af',
                                                                    fontWeight: 600,
                                                                    fontSize: '11px',
                                                                }}
                                                            >
                                                                {d.batch_number}
                                                            </Badge>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end" style={{ padding: '12px', fontWeight: 600, color: '#0ea5e9' }}>
                                                        {d.qty}
                                                    </td>
                                                    <td className="text-end" style={{ padding: '12px', color: '#1e293b' }}>
                                                        {new Intl.NumberFormat('id-ID').format(d.price || 0)}
                                                    </td>
                                                    <td className="text-end" style={{ padding: '12px', fontWeight: 700, color: '#1e293b' }}>
                                                        {new Intl.NumberFormat('id-ID').format(subtotal)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {(!selectedReturn.details || selectedReturn.details.length === 0) && (
                                            <tr>
                                                <td colSpan="6" className="text-center text-muted py-4">
                                                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
                                                    {t('Tidak ada item')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* === TOTAL SUMMARY === */}
                            {(() => {
                                // Hitung subtotal dari detail (qty × price)
                                const subtotal = (selectedReturn.details || []).reduce((sum, d) => {
                                    const q = parseFloat(d.qty) || 0;
                                    const p = parseFloat(d.price) || 0;
                                    return sum + (q * p);
                                }, 0);

                                const dpp = parseFloat(selectedReturn.dpp) || (subtotal * DPP_RATE_RATIO);
                                const tax = parseFloat(selectedReturn.tax_amount) || (dpp * (TAX_RATE_PERCENT / 100));
                                const grandTotal = parseFloat(selectedReturn.total_amount) || (subtotal + tax);

                                return (
                                    <Row className="justify-content-end mt-4">
                                        <Col md={6}>
                                            <div
                                                className="p-3 rounded"
                                                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                                            >
                                                {/* Subtotal */}
                                                <div className="d-flex justify-content-between py-1">
                                                    <span className="text-muted small">{t('Subtotal')}</span>
                                                    <strong style={{ fontSize: '13px' }}>
                                                        {new Intl.NumberFormat('id-ID').format(subtotal)}
                                                    </strong>
                                                </div>

                                                {/* DPP */}
                                                <div className="d-flex justify-content-between py-1">
                                                    <span className="text-muted small">
                                                        {t('DPP')} <small className="text-muted">(11/12)</small>
                                                    </span>
                                                    <strong style={{ fontSize: '13px' }}>
                                                        {new Intl.NumberFormat('id-ID').format(dpp)}
                                                    </strong>
                                                </div>

                                                {/* Tax */}
                                                <div className="d-flex justify-content-between py-1">
                                                    <span className="text-muted small">
                                                        {t('Tax')} <small className="text-muted">({TAX_RATE_PERCENT}%)</small>
                                                    </span>
                                                    <strong style={{ fontSize: '13px' }}>
                                                        {new Intl.NumberFormat('id-ID').format(tax)}
                                                    </strong>
                                                </div>

                                                {/* Grand Total */}
                                                <div
                                                    className="d-flex justify-content-between py-2 mt-1"
                                                    style={{ borderTop: '2px solid #e5e7eb' }}
                                                >
                                                    <span className="fw-bold">{t('Grand Total')}</span>
                                                    <strong className="text-primary" style={{ fontSize: '15px' }}>
                                                        {new Intl.NumberFormat('id-ID').format(grandTotal)}
                                                    </strong>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                );
                            })()}
                        </>
                    ) : (
                        <div className="text-center text-muted py-5">
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                            <div>{t('No data available')}</div>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter
                    style={{
                        background: '#fff',
                        borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px',
                        borderBottomLeftRadius: 'calc(0.5rem - 1px)',
                        borderBottomRightRadius: 'calc(0.5rem - 1px)',
                    }}
                >
                    {selectedReturn?.id && (
                        <Button
                            color="light"
                            onClick={() => handlePrintReturn(selectedReturn.id)}
                            className="d-flex align-items-center gap-2 border me-auto"
                            style={{ borderRadius: '8px', fontWeight: 600 }}
                        >
                            <Printer size={14} /> {t('Print Return')}
                        </Button>
                    )}
                    <Button
                        color="secondary"
                        onClick={() => setModalDetail(false)}
                        style={{ borderRadius: '8px', fontWeight: 600 }}
                    >
                        {t('Close')}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default PurchaseReturn;